import {
  ALERT_KINDS,
  type AlertFindingRow,
  type AlertKind,
  type AlertSeverity,
  type AlertSubjectType,
} from '@forecastify/shared';
import type { IconName } from '@forecastify/ui';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C59 · turning one stored finding into the four sentences a reader needs.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE BODY IS THE ONE THE MOCKUP COULD NOT GIVE US, AND THAT IS WORTH SAYING.** Its
 * `ALERT_KIND` catalogue carries a name, an impact and a fix per rule — those are in the catalogue
 * verbatim since C57. The per-finding sentence is different: in the prototype it was **hand-written
 * prose on each seed row** (*"Takács Gábor augusztus 17-én kétszer rögzített 3:00-t…"*), which a
 * mock with eight fixed findings can afford and a product with a live scan cannot.
 *
 * ⚠️ **So the body is `alert.<kind>.body` interpolated from the finding's own `values`**, which is
 * exactly what `scan.ts` says those values are for — *"already formatted, never raw numbers"*. The
 * subject label goes in as `{subject}` and every rule's template names only the keys its own
 * predicate emits.
 */

/**
 * ⚠⚠ **THE MOCKUP'S OWN `ALERT_SEV` COLOURS, IN ONE PLACE.** The row's dot and the detail
 * dialog's strip are the same statement about the same rule, and two copies of three hex values is
 * exactly how a list and the thing it opens end up disagreeing about what *magas* looks like.
 *
 * ⚠️ **`Record<AlertSeverity, string>`, so indexing is total** — with `noUncheckedIndexedAccess`
 * a looser record yields `string | undefined` and every use needs a fallback that can never fire.
 *
 * ⚠️ All three are tokens: the mockup's amber and grey are `--status-warn` and `--status-idle`.
 */
export const SEVERITY_TONE: Record<AlertSeverity, string> = {
  high: 'var(--destructive)',
  mid: 'var(--status-warn)',
  low: 'var(--status-idle)',
};

/** ⚠️ Severity is a property of the KIND, never stored per finding — one rule, one severity. */
export function severityOf(kind: AlertKind): AlertSeverity {
  return ALERT_KINDS[kind].severity;
}

export function penaltyOf(kind: AlertKind): number {
  return ALERT_KINDS[kind].penalty;
}

export function alertName(t: Translate, kind: AlertKind): string {
  return t(`alert.${kind}.name`);
}

export function alertImpact(t: Translate, kind: AlertKind): string {
  return t(`alert.${kind}.impact`);
}

export function alertFix(t: Translate, kind: AlertKind): string {
  return t(`alert.${kind}.fix`);
}

/**
 * ⚠️ **`{subject}` is always available; the rest come from the finding.** A template naming a key
 * the predicate did not emit would render the placeholder — which is why each one was written
 * against the `add(...)` call that produces it rather than against the rule's description.
 */
export function alertBody(t: Translate, finding: AlertFindingRow): string {
  return t(`alert.${finding.kind}.body`, {
    subject: finding.subjectLabel,
    ...finding.values,
  });
}

export function severityLabel(t: Translate, severity: AlertSeverity): string {
  return t(`alerts.severity.${severity}`);
}

export function areaLabel(t: Translate, kind: AlertKind): string {
  return t(`alerts.area.${ALERT_KINDS[kind].area}`);
}

/**
 * ⚠️⚠️ **A LOOKUP, NOT `icon as IconName`.** `ALERT_KINDS[kind].icon` is a `string`, because
 * `@forecastify/shared` cannot depend on `@forecastify/ui` and so cannot name that union. A cast
 * would compile for ever and render nothing the first time a rule is given an icon this set does
 * not have — the mockup's own catalogue already names seven, and `icon.d.ts` has ninety.
 *
 * ⚠️ **So the seven are mapped by hand and anything else falls back.** A missing icon is then a
 * generic dot rather than a blank space where a glyph should be.
 */
const KIND_ICONS: Record<string, IconName> = {
  'credit-card': 'credit-card',
  'calendar-days': 'calendar-days',
  'list-checks': 'list-checks',
  'file-text': 'file-text',
  clock: 'clock',
  'user-round': 'user-round',
  'layout-grid': 'layout-grid',
};

export function kindIcon(kind: AlertKind): IconName {
  return KIND_ICONS[ALERT_KINDS[kind].icon] ?? 'circle-dot';
}

/** The kebab's jump, named after what it opens rather than "Megnyitás" twice. */
export function goLabel(t: Translate, subjectType: AlertSubjectType): string {
  return t(`alerts.go.${subjectType}`);
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE JUMP OPENS THE RECORD, AND C59 SETTLED FOR LESS THAN IT COULD.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"A finding nobody can act on from the row is a list of complaints"* — so every subject resolves
 * to somewhere, and the kebab never offers a click that goes nowhere.
 *
 * ⚠️⚠️ **THE SERVER NOW SENDS THE ADDRESS, WHICH IS WHY THIS GOT SHORTER.** C59 sent line items,
 * contracts and costs to a project TAB, on the reading that those had no address of their own.
 * Three of them do — `/contracts/:code`, `/contracts/:code/:number`, `/costs/:number` — they just
 * need a code and a number the finding does not carry. `subjectPath` resolves that in the same
 * query that lists the findings, so this function only decides what to do when there is no path.
 * (R-57 · those routes take the identifier form and still accept a code or number, rewriting the
 * address to the identifier once resolved.)
 *
 * ⚠️ **A tab is the fallback, not the rule.** A worklog has no detail screen, and C56 left a
 * client-scoped contract without a code — in both cases the project tab that holds the record is
 * the honest destination.
 */
export type AlertTarget =
  { readonly kind: 'route'; readonly to: string } | { readonly kind: 'tab'; readonly tab: string };

export function alertTarget(
  finding: Pick<AlertFindingRow, 'subjectType' | 'subjectId' | 'key' | 'subjectPath'>,
): AlertTarget {
  /* ⚠️ The server's answer wins whenever it has one. */
  if (finding.subjectPath !== null && finding.subjectPath !== '') {
    return { kind: 'route', to: finding.subjectPath };
  }

  switch (finding.subjectType) {
    case 'line_item':
    case 'contract':
      /* A contract with no code — C56's client-scoped shape. */
      return { kind: 'tab', tab: 'contracts' };
    case 'external_cost':
      return { kind: 'tab', tab: 'costs' };
    case 'worklog':
      /* ⚠️ The one subject with no detail screen anywhere in the product. */
      return { kind: 'tab', tab: 'time' };
    default:
      return { kind: 'tab', tab: 'overview' };
  }
}
